import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { theme } from '../../styles/theme';
import loadingLottie from '../../assets/Icon/lottie/Insider-loading.lottie';

const stripEllipsis = (value: string) => value.replace(/\.+$/, '');

export type CancelSaveButtonsProps = {
  onCancel: () => void;
  onSave: () => void;
  saving?: boolean;
  savingLabel?: string;
  disabled?: boolean;
  platform: 'web' | 'mob';
  cancelLabel?: string;
  saveLabel?: string;
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

/* NewProject 웹 FooterBtn과 동일한 스타일 */
const CancelBtn = styled.button`
  display: flex;
  width: 130px;
  height: 54px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  border: 2px solid #DFDFDF;
  background: #FFFFFF;
  color: #676767;
  ${theme.typography.button.regular20}
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${theme.colors.grayScale[10]};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SaveBtn = styled.button<{ $saving?: boolean }>`
  display: flex;
  width: 130px;
  height: 54px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  border-radius: 4px;
  border: none;
  background: ${({ $saving, disabled }) => ($saving ? '#EBFCFF' : disabled ? '#DFDFDF' : '#026478')};
  color: ${({ $saving, disabled }) => ($saving ? '#2BC2E1' : disabled ? '#BCBCBC' : '#ffffff')};
  ${theme.typography.button.regular20}
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &:hover:not(:disabled) {
    background: ${({ $saving }) => ($saving ? '#EBFCFF' : theme.colors.primaryBlue[70])};
  }
`;

const LoadingIcon = styled(DotLottieReact)`
  display: inline-flex;
`;

const CancelSaveButtons = ({
  onCancel,
  onSave,
  saving = false,
  savingLabel,
  disabled = false,
  platform,
  cancelLabel,
  saveLabel,
}: CancelSaveButtonsProps) => {
  const { t } = useTranslation('translation');
  const resolvedSavingLabel = savingLabel ?? t('common.actions.saving');
  const resolvedCancelLabel = cancelLabel ?? t('common.actions.cancel');
  const resolvedSaveLabel = saveLabel ?? t('common.actions.save');
  return (
    <Wrapper>
      <CancelBtn type="button" onClick={onCancel} disabled={saving}>
        {resolvedCancelLabel}
      </CancelBtn>
      <SaveBtn
        type="button"
        onClick={onSave}
        disabled={disabled || saving}
        $saving={saving}
        aria-busy={saving}
        aria-label={saving ? stripEllipsis(resolvedSavingLabel) : resolvedSaveLabel}
      >
        {saving ? (
          <LoadingIcon
            src={loadingLottie}
            autoplay
            loop
            aria-hidden
            style={{ width: 12, height: 12 }}
          />
        ) : resolvedSaveLabel}
      </SaveBtn>
    </Wrapper>
  );
};

export default CancelSaveButtons;
